import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Sparkles, X, Copy } from "lucide-react";
import { toast } from "sonner";

const STORAGE_KEY = "folio.welcome_coupon_seen";
const LINGER_MS = 60 * 1000;  // 60s
const CODE = "WELCOME10";

/**
 * First-buy coupon nudge. Rules:
 * - Only shows on book detail pages (parent controls when it's mounted).
 * - Only if the user has never bought anything (or isn't signed in yet).
 * - Only if the user has never seen the coupon on this device (localStorage).
 * - Waits LINGER_MS on the same book detail page before appearing.
 */
export default function WelcomeCoupon() {
  const { user } = useAuth();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (localStorage.getItem(STORAGE_KEY) === "1") return;

    let mounted = true;
    let timer = null;

    const scheduleShow = () => {
      timer = setTimeout(() => {
        if (mounted) setVisible(true);
      }, LINGER_MS);
    };

    // If not signed in yet, we still show the coupon — signed-out visitors are the primary target.
    if (!user || user === false) {
      scheduleShow();
    } else {
      // Signed in — only show if they have zero purchases.
      api.get("/me/purchase-count").then((r) => {
        if (!mounted) return;
        if ((r.data?.count ?? 1) === 0) scheduleShow();
      }).catch(() => {});
    }

    return () => {
      mounted = false;
      if (timer) clearTimeout(timer);
    };
  }, [user]);

  const dismiss = () => {
    localStorage.setItem(STORAGE_KEY, "1");
    setVisible(false);
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(CODE);
      toast.success("Code copied — paste it at checkout");
    } catch {
      toast(CODE);
    }
    // Persist "seen" so the coupon doesn't reappear on the next book page.
    localStorage.setItem(STORAGE_KEY, "1");
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      data-testid="welcome-coupon"
      className="fixed bottom-6 left-6 z-40 w-[320px] bg-paper border border-terracotta shadow-xl animate-in slide-in-from-left-4 fade-in duration-500"
    >
      <button
        data-testid="welcome-coupon-dismiss"
        onClick={dismiss}
        className="absolute top-2 right-2 text-muted_ink hover:text-ink"
        aria-label="Dismiss"
      >
        <X className="w-4 h-4" />
      </button>
      <div className="p-6">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-terracotta" />
          <span className="label-eyebrow text-terracotta">A quiet gift</span>
        </div>
        <div className="font-serif text-2xl mt-2 leading-tight">
          Your first book, 10% off.
        </div>
        <p className="text-sm text-muted_ink mt-2">
          Use the code at checkout. No expiry, no strings.
        </p>
        <button
          data-testid="welcome-coupon-copy"
          onClick={copy}
          className="mt-4 w-full border border-terracotta bg-linen font-mono text-lg tracking-[0.2em] py-3 hover:bg-terracotta hover:text-paper transition-colors flex items-center justify-center gap-2"
        >
          {CODE}
          <Copy className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}
